import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import type { AugmentedGoal, GoalOutcome } from 'src/app/domain/goals/types/Goal.model';
import { hasAnyProgressSignal, hasOffTrackProgress } from 'src/app/domain/goals/services/goal-utils';
import { BadgeComponent, BadgeVariant } from 'src/app/ui-kit/badge/badge.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/** Visual progress trajectory shown as the trailing badge on the row. */
interface ProgressBadge {
  label: string;
  variant: BadgeVariant;
}

/**
 * Dense list-row for an {@link AugmentedGoal}. Renders a leading lifecycle
 * dot (matching the caseload section header), the title + skill subtitle,
 * a trailing progress badge for active goals, and a chevron when clickable.
 *
 * @security FERPA — goal content is IEP-adjacent.
 *
 * @feature shared
 */
@Component({
  selector: 'app-goal-list-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <button
      type="button"
      class="flex w-full rounded-lg text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
      [disabled]="!clickable()"
      [class.cursor-default]="!clickable()"
      (click)="onClick()"
      [attr.aria-label]="'Open goal ' + title()"
    >
      <div class="min-w-0 flex-1 py-4 pl-4 space-y-1">
        <p class="truncate text-base font-semibold text-body">{{ title() }}</p>
        @if (subtitle()) {
          <p class="truncate text-sm text-muted">{{ subtitle() }}</p>
        }
      </div>

      @if (progressBadge(); as badge) {
        <div class="flex items-center px-3 self-stretch">
          <app-badge [variant]="badge.variant" size="xs">{{ badge.label }}</app-badge>
        </div>
      }

      @if (clickable()) {
        <div class="flex items-center px-3 self-stretch">
          <app-icon name="chevron-forward-outline" size="xs" color="medium" />
        </div>
      }
    </button>
  `,
})
export class GoalListItemComponent {
  /** The augmented goal to display. */
  readonly goal = input.required<AugmentedGoal>();

  /** Optional student name used to format the title (`"<Full Name>'s <Area> Goal"`). */
  readonly studentName = input<string | undefined>(undefined);

  /** Whether the row is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the row is clicked. */
  readonly rowClick = output<AugmentedGoal>();

  readonly title = computed(() => {
    const g = this.goal();
    const fullName = (this.studentName() ?? '').trim();
    const area = g.skillArea?.trim() || 'Goal';
    return fullName ? `${fullName}'s ${area} Goal` : `${area} Goal`;
  });

  /**
   * Total observations recorded for the goal, keyed by skillId. Sums
   * baseline-phase and progress-monitoring observations (the augmentation
   * keeps these in separate arrays — `_baselineObservations` for the
   * snapshot frozen at finalize time, `_observations` for everything after).
   * Deduplicated by observation id in case the same record ever appears in
   * both arrays.
   */
  readonly observationCountsBySkill = computed<Map<string, number>>(() => {
    const counts = new Map<string, number>();
    const seen = new Set<string>();
    const g = this.goal();
    const all = [...(g._baselineObservations ?? []), ...(g._observations ?? [])];
    for (const obs of all) {
      // `Observation.id` is optional on the type — fall back to a positional
      // key so observations without a populated id still get counted (and
      // can't collide with each other in the dedup `seen` set).
      const key = obs.id ?? `__pos:${seen.size}`;
      if (seen.has(key)) continue;
      seen.add(key);
      counts.set(obs.skillId, (counts.get(obs.skillId) ?? 0) + 1);
    }
    return counts;
  });

  /**
   * Comma-separated list of the goal's skills, each followed by an
   * observation count in parentheses (`Reading Decoding (5)`). 0-counts
   * are kept so a skill with no observations is still visible — that's
   * actionable information ("nothing collected yet for this skill"), not
   * noise. Skill ids without a display-name snapshot fall back to the raw
   * skillId so the row still renders cleanly.
   */
  readonly subtitle = computed(() => {
    const g = this.goal();
    const counts = this.observationCountsBySkill();
    const entries: string[] = [];
    const skillIds = g.skillIds ?? [];
    for (let i = 0; i < skillIds.length; i++) {
      const id = skillIds[i];
      const name = g.skills?.[i] || id;
      if (!name) continue;
      const count = counts.get(id) ?? 0;
      entries.push(`${name} (${count})`);
    }
    return entries.join(', ');
  });

  /**
   * Trailing badge that surfaces the goal's status. Pure performance signal:
   *   - Draft + needs-baseline: 'Needs data' (danger) — fresh draft, no
   *     baseline measurements yet; styled as an alert so it stands out.
   *   - Draft + building-baseline (partial): 'Building baseline' (secondary)
   *     — at least one baseline pair has data, still collecting.
   *   - Draft + building-baseline (complete): 'Ready to finalize' (success)
   *     — every tracked pair has a data point; targets can now be set.
   *   - Active goals: 'Behind' (warning) / 'On Track' (primary) based on
   *     {@link hasOffTrackProgress}, which evaluates measurement-type
   *     `_progress.onTrack` AND per-skill support-level
   *     `_supportLevelProgress.aggregate.onTrack`. Either branch flips the
   *     badge to "Behind". Observation overdue (`_isOverdue`) is a
   *     scheduling concern and intentionally does NOT affect this badge.
   *   - Active goals with no progress signal yet: no badge (would otherwise
   *     misleadingly read as "On Track").
   *   - Completed goals: 'Achieved' / 'Missed' / 'No data' from `_outcome`.
   */
  readonly progressBadge = computed<ProgressBadge | null>(() => {
    const g = this.goal();
    if (g._lifecycleBucket === 'needs-baseline') return { label: 'Needs data', variant: 'danger' };
    if (g._lifecycleBucket === 'building-baseline') {
      if (g._baselineStatus?.complete) return { label: 'Ready to finalize', variant: 'success' };
      return { label: 'Building baseline', variant: 'secondary' };
    }
    if (g._lifecycleBucket === 'progress-monitoring') {
      if (!hasAnyProgressSignal(g)) return null;
      if (hasOffTrackProgress(g)) return { label: 'Behind', variant: 'warning' };
      return { label: 'On Track', variant: 'primary' };
    }
    if (g._lifecycleBucket === 'completed') {
      return outcomeBadge(g._outcome);
    }
    return null;
  });

  onClick(): void {
    if (this.clickable()) {
      this.rowClick.emit(this.goal());
    }
  }
}

/** Map a completed goal's outcome to a trailing-badge config. */
function outcomeBadge(outcome: GoalOutcome | null): ProgressBadge | null {
  switch (outcome) {
    case 'achieved':
      return { label: 'Achieved', variant: 'success' };
    case 'missed':
      return { label: 'Missed', variant: 'danger' };
    case 'no-data':
      return { label: 'No data', variant: 'muted' };
    default:
      return null;
  }
}
